import { useState, useEffect } from "react";
import { all_routes } from "../../../feature-module/router/all_routes";
import { API_URL } from '../../../environment';
// Reativado em 2026-05-29: usado pelo botão "Monitoramento" da sidebar (ver abaixo).
import { canViewDashboardTnp } from "../../../feature-module/mainMenu/dashboardTnp/access";
// Botão "Templates com Erro" — visível apenas para o time 159.
import { canViewTemplatesComErro } from "../../../feature-module/application/templatesComErro/access";
// Botão "Leads do Type" — admins dos grupos com funil no type.
import { canViewLeadsType } from "../../../feature-module/application/leadsType/access";
// Botão "Discadora" — desde 05/11/2026 pela tabela funcionalidade_conta (discadora / discadora_ligar).
import { canViewDiscadora } from "../../../feature-module/application/discadora/access";
import { carregarFuncionalidades, EVENTO_FUNCIONALIDADES } from "../../../feature-module/application/funcionalidades";

const routes = all_routes;
const sidebarFetchCache = new Map<string, { data: any, timestamp: number }>();
const pendingSidebarRequests = new Map<string, Promise<any>>();
const CACHE_TTL = 10000;

export const sharedSidebarFetch = async (url: string, body: any, signal?: AbortSignal) => {
    const key = url + JSON.stringify(body);
    const now = Date.now();
    
    // 1. Check Cache
    const cached = sidebarFetchCache.get(key);
    if (cached && (now - cached.timestamp < CACHE_TTL)) return cached.data;
    
    // 2. Check Pending (Deduplication)
    if (pendingSidebarRequests.has(key)) return pendingSidebarRequests.get(key);
    
    const fetchPromise = (async () => {
        try {
            const response = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
                signal
            });
            if (!response.ok) return [];
            const data = await response.json();
            sidebarFetchCache.set(key, { data, timestamp: Date.now() });
            return data;
        } catch (e) {
            if (e instanceof Error && e.name === "AbortError") throw e;
            return [];
        } finally {
            pendingSidebarRequests.delete(key);
        }
    })();

    pendingSidebarRequests.set(key, fetchPromise);
    return fetchPromise;
};




export interface SubMenu {
  menuValue: string;
  route: string;
  children?: SubMenu[];
  params?: any;
  isPlaceholder?: boolean; // Novo campo para identificar carregamento
}

export interface SidebarMenu {
  menuValue: string;
  icon: string;
  subMenus?: SubMenu[];
  route?: string;
  isLoading?: boolean; // Novo campo para estado de carregamento do pai
}

export const useSidebarData = () => {
  const isAdmin = localStorage.getItem("isAdmin") === "true";
  const isAdminMaster = localStorage.getItem("isAdminMaster") === "true";
  const idcliente = localStorage.getItem("id_cliente");
  // "Instância oficial configurada": o login só grava `instanciaoficial` no
  // localStorage quando há WABA (valor != "0"). Mesmo critério usado no /chat.
  const instanciaOficial = localStorage.getItem("instanciaoficial");
  const temInstanciaOficial = !!instanciaOficial && instanciaOficial !== "0";

  const [sidebarData, setSidebarData] = useState<SidebarMenu[]>(() => {
    // Definimos a estrutura base IMEDIATAMENTE (Sem Loading)
    const baseMenu: SidebarMenu[] = [
      // "Dashboard" (/leads-dashboard) — NÃO entra aqui: só é adicionado no useEffect
      // abaixo, e apenas para logins que TÊM SDR (chats com cliente '[SDR] …'). Como
      // "ter SDR" é derivado de dado (não é flag de login), começa ausente e o fetch
      // de /dashboard/tem_sdr injeta o item quando confirma — nunca aparece p/ não-SDR.
      { menuValue: "Chat", icon: "message-circle", route: routes.chat },
      { menuValue: "Biblioteca", icon: "photo", route: routes.bibliotecaMidias },
      { menuValue: "Respostas Rápidas", icon: "bolt", route: routes.respostasRapidas },
      { menuValue: "Lista de Leads", icon: "users", route: routes.leadsList }, // BOTÃO DIRETO 🚀
    ];

    // Dashboard TNP — visível apenas para o time TNP (id_cliente === 146)
    // Reativado em 2026-05-29 após corrigir permissões do dashboard estático em produção.
    if (canViewDashboardTnp()) {
      baseMenu.push({ menuValue: "Monitoramento", icon: "chart-bar", route: routes.dashboardTnp });
    }

    // Dashboard do cliente (app dash-clientes, multi-tenant) — para TODOS os grupos, MENOS
    // o 192 (TNP). A TNP tem a dash própria dela (com performance e retenção) e o item de
    // menu atual; acrescentar um segundo botão lá mudaria a tela de quem já usa a de hoje.
    // Nascimento Martins (9184) e Thiago Castro (9189): o botão "Dashboard" de todos os logins
    // leva à dash de gestão deles (dash.conversaicrm.com.br/dashboard/clientes/, nova aba, login
    // próprio da dash). Botão único: o antigo "Dashboard Gestão" foi removido em 29/10/2026.
    if (["9184", "9189"].includes(localStorage.getItem("id_grupo") || "")) {
      baseMenu.push({ menuValue: "Dashboard", icon: "chart-histogram", route: "https://dash.conversaicrm.com.br/dashboard/clientes/" });
    } else if (localStorage.getItem("id_grupo") !== "192") {
      baseMenu.push({ menuValue: "Dashboard", icon: "chart-histogram", route: routes.dashboardCliente });
    }

    // Dashboard externa (dash.conversaicrm.com.br/dashboard/tnp/) — p/ logins ADMIN do
    // grupo 192 (TNP) OU qualquer login do grupo com a flag `dashboard_tnp` do backend
    // (2026-07-31: as 4 atendentes da RETENÇÃO, que são operadoras e por isso nunca viam
    // este item). Rota http: o menu abre em nova aba (onClick). O token (SSO) vai na URL
    // quando o login tem; a dash valida e libera o acesso.
    // Quem é só da retenção não fica no funil: o index.html do dash-auxílio desvia para
    // /dashboard/tnp/retencao/.
    //
    // ⚠️ A cláusula da flag existia SÓ NO AR: foi aplicada como patch manual direto no
    // bundle minificado em 2026-07-31 14:42 (backup .bak-20260731-144250 ao lado), nunca
    // entrou no git. Trazida pro source em 04/08 na Story 13 para o build novo não apagar
    // o acesso de quem depende dela. Se a intenção era temporária, remover aqui.
    if (
      (isAdmin || isAdminMaster || localStorage.getItem("dashboard_tnp") === "true") &&
      localStorage.getItem("id_grupo") === "192"
    ) {
      const dashTk = localStorage.getItem("dash_tnp_token") || "";
      const dashUrl = "https://dash.conversaicrm.com.br/dashboard/tnp/" + (dashTk ? `?t=${encodeURIComponent(dashTk)}` : "");
      baseMenu.push({ menuValue: "Dashboard TNP", icon: "chart-bar", route: dashUrl });
    }

    // Dashboard AFH (dash.conversaicrm.com.br/dashboard/afh/) — só p/ ADMIN do grupo 188.
    // Sem coluna nova no banco: regra puramente no sidebar (id_grupo 188 + admin). Rota http
    // (abre em nova aba, igual a TNP).
    if ((isAdmin || isAdminMaster) && localStorage.getItem("id_grupo") === "188") {
      baseMenu.push({ menuValue: "Dashboard AFH", icon: "chart-bar", route: "https://dash.conversaicrm.com.br/dashboard/afh/" });
    }

    // Leads do Type — conferência type × CRM (admins de Nascimento, Buchele e Thiago).
    if (canViewLeadsType()) {
      baseMenu.push({ menuValue: "Leads do Type", icon: "filter-check", route: routes.leadsType });
    }

    // Discadora — liga/desliga na tabela funcionalidade_conta (aqui vale a última leitura guardada;
    // o efeito abaixo acerta o item quando a leitura do servidor chega).
    if (canViewDiscadora()) {
      baseMenu.push({ menuValue: "Discadora", icon: "phone-call", route: routes.discadora });
    }

    // Templates com Erro — visível apenas para o time 159 (auditoria de falhas de template).
    if (canViewTemplatesComErro()) {
      baseMenu.push({ menuValue: "Templates com Erro", icon: "alert-triangle", route: routes.templatesComErro });
    }

    // Menu de Administração (se for admin)
    const adminSubMenus: SubMenu[] = [];
    if (isAdmin || isAdminMaster) {
      adminSubMenus.push({ menuValue: "Gerenciamento de Equipe (Conexões)", route: routes.teamUserManagement });
    }
    if (isAdminMaster) {
      adminSubMenus.push({ menuValue: "Criador de Cards (Admin)", route: routes.cardBuilderAdmin });
      adminSubMenus.push({ menuValue: "Gestão de Funis (Admin)", route: routes.todoAdmin });
    }
    // Templates WhatsApp: admin/admin master E com instância oficial (WABA) configurada.
    if ((isAdmin || isAdminMaster) && temInstanciaOficial) {
      adminSubMenus.push({ menuValue: "Templates WhatsApp", route: routes.whatsappTemplates });
    }

    // Admin de time: Criador de Cards + Editar Funis/Etapas TAMBÉM agrupados em "Administração"
    // (antes ficavam soltos no topo e deixavam o sidebar muito grande).
    if (isAdmin && !isAdminMaster) {
      adminSubMenus.push({ menuValue: "Criador de Cards", route: routes.cardBuilder });
      adminSubMenus.push({ menuValue: "Editar Funis/Etapas", route: "/todo" });
    }

    if (adminSubMenus.length > 0) {
      baseMenu.push({
        menuValue: "Administração",
        icon: "settings",
        subMenus: adminSubMenus,
      });
    }

    return baseMenu;
  });

  // 05/11/2026: funções por conta (tabela funcionalidade_conta). O item "Discadora" segue a leitura do
  // servidor: a cópia guardada pode estar velha ou vazia na primeira abertura depois do deploy.
  useEffect(() => {
    let vivo = true;
    const aplicar = () => {
      if (!vivo) return;
      const ver = canViewDiscadora();
      setSidebarData((prev) => {
        const tem = prev.some((m) => m.route === routes.discadora);
        if (ver === tem) return prev;
        if (!ver) return prev.filter((m) => m.route !== routes.discadora);
        const item: SidebarMenu = { menuValue: "Discadora", icon: "phone-call", route: routes.discadora };
        const adm = prev.findIndex((m) => m.menuValue === "Administração");
        return adm < 0 ? [...prev, item] : [...prev.slice(0, adm), item, ...prev.slice(adm)];
      });
    };
    carregarFuncionalidades().then(aplicar);
    window.addEventListener(EVENTO_FUNCIONALIDADES, aplicar);
    return () => {
      vivo = false;
      window.removeEventListener(EVENTO_FUNCIONALIDADES, aplicar);
    };
  }, []);

  useEffect(() => {
    console.log("✅ Sidebar Leads 2.0 inicializada com sucesso!");

    // "Dashboard" (/leads-dashboard) só existe para logins que têm SDR. Buscamos
    // /dashboard/tem_sdr e, se positivo, injetamos o item no topo do menu (idempotente).
    if (!idcliente) return;
    fetch(`${API_URL}/dashboard/tem_sdr?id_time=${idcliente}`)
      .then((r) => r.json())
      .then((d) => {
        if (!d?.tem_sdr) return;
        setSidebarData((prev) =>
          prev.some((m) => m.route === routes.leadsDashboard)
            ? prev
            : [{ menuValue: "Dashboard", icon: "calendar-event", route: routes.leadsDashboard }, ...prev]
        );
      })
      .catch(() => {});
  }, [idcliente]);

  return sidebarData;
};
